import React, { useState } from 'react';
import { PanelShell } from '../ui/PanelShell';
import { SectionTitle } from '../ui/SectionTitle';
import { FieldRow } from '../ui/FieldRow';
import { NumberField } from '../ui/NumberField';
import { PillButton } from '../ui/PillButton';
import { InfoTip } from '../ui/InfoTip';
type LabelPanelProps = {
  onClose: () => void;
};
type Mode = 'emboss' | 'engrave';
const fonts = ['JetBrains Mono', 'Roboto Mono', 'IBM Plex Mono', 'Source Code Pro'];
export function LabelPanel({
  onClose
}: LabelPanelProps) {
  const [text, setText] = useState('');
  const [font, setFont] = useState(fonts[0]);
  const [mode, setMode] = useState<Mode>('emboss');
  const [size, setSize] = useState(2);
  const [depth, setDepth] = useState(0.5);
  return <PanelShell title="Label" onClose={onClose} footer={<>
          <PillButton disabled={!text} onClick={() => setText('')} data-id="element-363" data-mp="3f8ar3pbyy8pz">
            Delete
          </PillButton>
          <PillButton variant={text ? 'primary' : 'muted'} disabled={!text} data-id="element-364" data-mp="3a7wfxyf2minq">
            Apply Edits
          </PillButton>
        </>} data-id="element-362" data-mp="1fy18tev6v6cx">
      
      <SectionTitle data-id="element-365" data-mp="3jtwhnicp8ghz">Edit Label</SectionTitle>
      <input aria-label="Label text" value={text} onChange={e => setText(e.target.value)} className="h-[22px] w-full rounded-[4px] border border-pf-line bg-[#f1f1f3] px-2 text-[12px] text-pf-ink outline-none focus:border-pf-blue-line focus:bg-white" data-id="element-366" data-mp="3juxr5ie2guxl" />
      
      <div className="flex min-h-[38px] items-center justify-between" data-id="element-367" data-mp="2dy49nasvp10">
        <div className="flex items-center gap-[5px] text-[12px] text-pf-muted" data-id="element-368" data-mp="6dorxi515p1b">
          Text Font <InfoTip label="About text font" data-id="element-369" data-mp="3otis5k157in3" />
        </div>
        <div className="relative h-[28px] w-[173px]" data-id="element-370" data-mp="3oqwlrnpzn96g">
          <select aria-label="Text font" value={font} onChange={e => setFont(e.target.value)} className="h-full w-full appearance-none rounded-[4px] border border-pf-line bg-white pl-2 pr-6 font-mono text-[12.5px] text-pf-ink outline-none" data-id="element-371" data-mp="3tqj1c3tah1if">
            
            {fonts.map(f => <option key={f} data-id="element-372" data-mp="zwmukwmamzpe">{f}</option>)}
          </select>
          <svg viewBox="0 0 8 5" className="pointer-events-none absolute right-[9px] top-1/2 h-[4px] w-[7px] -translate-y-1/2 text-[#5b6066]" fill="currentColor" data-id="element-373" data-mp="v05zfigofl2t">
            <path d="M0 0 H8 L4 5 Z" data-id="element-374" data-mp="l1ymrbtf7p6m" />
          </svg>
        </div>
      </div>
      <div role="radiogroup" aria-label="Label style" className="grid grid-cols-2 gap-[8px]" data-id="element-375" data-mp="gzv6m9vt3wf0">
        {(['emboss', 'engrave'] as const).map(m => {
        const active = mode === m;
        return <button key={m} type="button" role="radio" aria-checked={active} onClick={() => setMode(m)} className={`flex h-[80px] flex-col items-center justify-center gap-[6px] rounded-[6px] border transition-colors duration-150 ${active ? 'border-pf-blue bg-pf-blue-soft text-pf-blue' : 'border-pf-line bg-white text-pf-muted hover:bg-[#fafafa]'}`} data-id="element-376" data-mp="79scu7gv2efm">
              
              <svg viewBox="0 0 40 34" className="h-[26px] w-[30px]" data-id="element-377" data-mp="28z7qm0i06lbj">
                <path d="M4 14 L20 7 L36 14 V24 L20 31 L4 24 Z" fill="#c9ccd1" stroke="#7d8288" strokeWidth="1.2" strokeLinejoin="round" data-id="element-378" data-mp="28xeishvnlv1y" />
                <path d="M4 14 L20 21 L36 14 M20 21 V31" fill="none" stroke="#7d8288" strokeWidth="1.2" data-id="element-379" data-mp="2ix5egmvyag3m" />
                {m === 'emboss' ? <g data-id="element-380" data-mp="1lay8p9nwuhpo">
                    <path d="M14 8 L20 5 L26 8 V14 L20 17 L14 14 Z" fill="#3a9be0" stroke="#1f6fb5" strokeWidth="1" data-id="element-381" data-mp="1lbgdwvsjw69q" />
                    <path d="M14 8 L20 11 L26 8 M20 11 V17" fill="none" stroke="#1f6fb5" strokeWidth="1" data-id="element-382" data-mp="1q6dj1rmplvhc" />
                  </g> : <path d="M13 14 L20 11 L27 14 L20 17 Z" fill="#3a9be0" stroke="#1f6fb5" strokeWidth="1" data-id="element-383" data-mp="1zz1jcwcwehkt" />}
              </svg>
              <span className="text-[12.5px] capitalize" data-id="element-384" data-mp="12ivoxpfz2lb7">{m}</span>
            </button>;
      })}
      </div>

      <SectionTitle data-id="element-385" data-mp="17g5mjsdim3a4">Label Geometry</SectionTitle>
      <FieldRow label="Text Size" info data-id="element-386" data-mp="396mmakx7oh2j">
        <NumberField ariaLabel="Text size" value={size} onChange={setSize} decimals={1} unit="mm" min={1} max={20} className="w-[96px]" data-id="element-387" data-mp="397nywttvlbmd" />
      </FieldRow>
      <input aria-label="Text size slider" type="range" className="pf-range my-[6px]" min={1} max={20} step={0.1} value={size} onChange={e => setSize(Number(e.target.value))} data-id="element-388" data-mp="3ixqxd9jrnhs3" />
      <FieldRow label="Depth" info className="mt-[4px]" data-id="element-389" data-mp="3svdl8lj1mjo7">
        <NumberField ariaLabel="Depth" value={depth} onChange={setDepth} unit="mm" step={0.05} min={0.1} max={5} className="w-[96px]" data-id="element-390" data-mp="3stu1gky6hqho" />
      </FieldRow>
      <input aria-label="Depth slider" type="range" className="pf-range my-[6px]" min={0.1} max={5} step={0.05} value={depth} onChange={e => setDepth(Number(e.target.value))} data-id="element-391" data-mp="5r7ezrg7wll3" />
    </PanelShell>;
}